'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import type { ReactNode, Ref } from 'react';
import { useState } from 'react';
import { MobileSearchOverlay, RecentSearchChips, SearchBar, useHideOnScroll } from '@/app/_components/SearchBar';
import type { SearchScope } from '@/app/_components/SearchBar/constants';
import { BREAKPOINTS } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { clearOverlayHistoryEntry, useOverlayHistory } from '@/hooks/useOverlayHistory';
interface SearchPageHeaderProps {
  keyword: string;
  filterCount: number;
  recentKeywords: string[];
  scope?: SearchScope;
  onSearch: (keyword: string) => void;
  onFilterClick?: () => void;
  onSelectRecent: (keyword: string) => void;
  onRemoveRecent: (keyword: string) => void;
  onClearRecent: () => void;
  //* 노출 판정은 소비처 몫 — 자리 예약 높이가 그 판정에 묶여 있어 여기서 다시 계산하면 두 값이 갈린다.
  trendKeyword?: ReactNode;
  ref?: Ref<HTMLDivElement>;
}

/**
 * 모바일: `position: fixed` 오버레이로 상단 고정 + 스크롤 방향에 따라 translate 숨김.
 * PC: 일반 flow. spacer는 mobile-only.
 * ref는 외부 wrapper에 연결되어 IntersectionObserver의 관찰 대상이 된다
 * (모바일에서는 spacer 높이 = in-flow 기준으로 가시성을 판단).
 */
export const SearchPageHeader = t0 => {
  const $ = _c(44);
  const {
    keyword,
    filterCount,
    recentKeywords,
    scope,
    onSearch,
    onFilterClick,
    onSelectRecent,
    onRemoveRecent,
    onClearRecent,
    trendKeyword,
    ref
  } = t0;
  const [isFocused, setIsFocused] = useState(false);
  const [isSearchLayerOpen, setIsSearchLayerOpen] = useState(false);
  const isMobileTabletViewport = useMediaQuery(BREAKPOINTS.MOBILE_TABLET);
  const {
    hidden
  } = useHideOnScroll();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setIsSearchLayerOpen(false);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const closeSearchLayer = t1;
  const isSearchLayerVisible = isSearchLayerOpen && isMobileTabletViewport;
  useOverlayHistory(isSearchLayerVisible, closeSearchLayer);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = handler => keyword_0 => {
      clearOverlayHistoryEntry();
      handler(keyword_0);
      setIsSearchLayerOpen(false);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const searchFromLayer = t2;
  let t3;
  if ($[2] !== isMobileTabletViewport) {
    t3 = focused => {
      setIsFocused(focused);
      if (focused && isMobileTabletViewport) {
        setIsSearchLayerOpen(true);
      }
    };
    $[2] = isMobileTabletViewport;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleFocusChange = t3;
  const shouldHide = hidden && !isFocused;
  const spacerHeightClassName = trendKeyword ? "mobile-tablet:h-98" : "mobile-tablet:h-48";
  const t4 = shouldHide && "mobile-tablet:-translate-y-full";
  let t5;
  if ($[4] !== t4) {
    t5 = cn("flex w-full flex-col items-center gap-12 bg-Gray-white", "mobile-tablet:fixed mobile-tablet:inset-x-0 mobile-tablet:top-0 mobile-tablet:z-10 mobile-tablet:gap-0", "mobile-tablet:transition-transform mobile-tablet:duration-200", t4);
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== filterCount || $[7] !== handleFocusChange || $[8] !== keyword || $[9] !== onFilterClick || $[10] !== onSearch || $[11] !== scope) {
    t6 = <SearchBar keyword={keyword} filterCount={filterCount} sticky={false} scope={scope} onSearch={onSearch} onFilterClick={onFilterClick} onFocusChange={handleFocusChange} />;
    $[6] = filterCount;
    $[7] = handleFocusChange;
    $[8] = keyword;
    $[9] = onFilterClick;
    $[10] = onSearch;
    $[11] = scope;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== onClearRecent || $[14] !== onRemoveRecent || $[15] !== onSelectRecent || $[16] !== recentKeywords) {
    t7 = recentKeywords.length > 0 && <div className="hidden w-full xl:block"><RecentSearchChips keywords={recentKeywords} onSelect={onSelectRecent} onRemove={onRemoveRecent} onClearAll={onClearRecent} /></div>;
    $[13] = onClearRecent;
    $[14] = onRemoveRecent;
    $[15] = onSelectRecent;
    $[16] = recentKeywords;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== trendKeyword) {
    t8 = trendKeyword && <div className="hidden w-full mobile-tablet:block">{trendKeyword}</div>;
    $[18] = trendKeyword;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t5 || $[21] !== t6 || $[22] !== t7 || $[23] !== t8) {
    t9 = <div className={t5}>{t6}{t7}{t8}</div>;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== spacerHeightClassName) {
    t10 = cn("hidden mobile-tablet:block", spacerHeightClassName);
    $[25] = spacerHeightClassName;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10) {
    t11 = <div aria-hidden={true} className={t10} />;
    $[27] = t10;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== isMobileTabletViewport || $[30] !== isSearchLayerVisible || $[31] !== keyword || $[32] !== onClearRecent || $[33] !== onRemoveRecent || $[34] !== onSearch || $[35] !== onSelectRecent || $[36] !== recentKeywords || $[37] !== scope) {
    t12 = isMobileTabletViewport && <MobileSearchOverlay isOpen={isSearchLayerVisible} keyword={keyword} scope={scope} recentKeywords={recentKeywords} onClose={closeSearchLayer} onSearch={searchFromLayer(onSearch)} onSelectRecent={searchFromLayer(onSelectRecent)} onRemoveRecent={onRemoveRecent} onClearRecent={onClearRecent} />;
    $[29] = isMobileTabletViewport;
    $[30] = isSearchLayerVisible;
    $[31] = keyword;
    $[32] = onClearRecent;
    $[33] = onRemoveRecent;
    $[34] = onSearch;
    $[35] = onSelectRecent;
    $[36] = recentKeywords;
    $[37] = scope;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== ref || $[40] !== t11 || $[41] !== t12 || $[42] !== t9) {
    t13 = <div ref={ref} className="w-full flex-1">{t9}{t11}{t12}</div>;
    $[39] = ref;
    $[40] = t11;
    $[41] = t12;
    $[42] = t9;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  return t13;
};